import type { Component, Ref } from 'vue'
import type { ComponentProps, ComponentSlots } from 'vue-component-type-helpers'

import { computed, markRaw, ref, toRaw, watch } from 'vue'

/**
 * How many modals of a stack are rendered at once. The ones beyond the cap stay
 * on the stack but are not mounted, so they lose their component state until
 * they come back into range.
 */
export const MODAL_STACK_MAX_SIZE = 3

type ModalHandlers = {
  onCancel?: () => void
  onClose?: () => void
  onConfirm?: () => void
}

/**
 * Opting into `stack` makes a modal render alongside the other stackable modals
 * instead of suppressing them. Position is decided by `priority` first (higher
 * sits on top) and by the moment the modal was triggered second, so modals that
 * share a priority stack up in the order they appeared.
 */
type ModalStacking = {
  stack?: boolean
}

export type ModalConfig<C extends Component = Component> = ModalHandlers &
  ModalStacking & {
    component: C
    id?: string
    persistent?: boolean
    priority?: number
    props?: ComponentProps<C>
    routePath?: string
    slots?: Partial<ComponentSlots<C>>
  }

export type ModalEntry = ModalConfig & { activatedAt: number; id: string }

export type ModalRegistration<C extends Component = Component> = ModalHandlers &
  ModalStacking & {
    component: C
    id?: string
    priority?: number
    props?: () => ComponentProps<C> | null
    slots?: Partial<ComponentSlots<C>>
    when?: () => boolean
  }

export type StoredRegistration = ModalRegistration & { id: string }

export type ResolvedModal = {
  component: Component
  depth: number
  id: string
  props?: Record<string, unknown>
  slots?: Record<string, unknown>
}

type ModalCallback = keyof ModalHandlers

type ModalProps = Record<string, unknown> | undefined

/** When each registration last started to show, by id. See `nextActivation`. */
export type ModalActivations = Record<string, number>

type ModalCandidate = {
  /** Ranks modals of equal priority: the one triggered last comes first. */
  activatedAt: number
  modal: Omit<ResolvedModal, 'depth'>
  priority: number
  stack: boolean
}

const nextActivation = (() => {
  let activation = 0
  return () => (activation += 1)
})()

/**
 * Stops the eligibility watcher of a registration that is being replaced or
 * unregistered. Keyed by the stored registration itself, so a replaced entry
 * takes its watcher with it when it is collected.
 */
const activationWatchers = new WeakMap<StoredRegistration, () => void>()

const topmostFirst = (a: ModalCandidate, b: ModalCandidate) =>
  b.priority - a.priority || b.activatedAt - a.activatedAt

const NOT_ELIGIBLE = Symbol('modal-not-eligible')

const eligibleProps = (registration: StoredRegistration): ModalProps | typeof NOT_ELIGIBLE => {
  if (registration.when && !registration.when()) return NOT_ELIGIBLE

  const props = registration.props?.()

  return props === null ? NOT_ELIGIBLE : props
}

const toCandidate = (
  { component, id, priority, slots, stack }: ModalEntry | StoredRegistration,
  props: ModalProps,
  activatedAt: number,
): ModalCandidate => ({
  activatedAt,
  modal: { component, id, props, slots: slots as ModalProps },
  priority: priority ?? 0,
  stack: Boolean(stack),
})

export const useModalIsolated = (
  activeModals: Ref<ModalEntry[]>,
  registrations: Ref<StoredRegistration[]> = ref([]),
  activations: Ref<ModalActivations> = ref({}),
) => {
  /*
   * A registration is triggered whenever its `when` predicate starts to hold,
   * which can be long after it was registered, so the moment is recorded per
   * registration by its own watcher rather than while resolving the stack. The
   * stamp is dropped again once the modal stops showing, so re-triggering it
   * moves it back to the front.
   */
  const watchActivation = (registration: StoredRegistration) =>
    watch(
      () => eligibleProps(registration) !== NOT_ELIGIBLE,
      (isShowing) => {
        const { [registration.id]: previous, ...rest } = activations.value

        activations.value = isShowing
          ? { ...rest, [registration.id]: previous ?? nextActivation() }
          : rest
      },
      { flush: 'sync', immediate: true },
    )

  const openModal = <C extends Component>(config: ModalConfig<C>) => {
    const id = config.id ?? uniqueId('modalisolated-')
    if (activeModals.value.some((entry) => entry.id === id)) return id

    const entry: ModalEntry = {
      ...(config as ModalConfig),
      activatedAt: nextActivation(),
      component: markRaw(config.component as object) as Component,
      id,
    }

    activeModals.value = config.stack ? [...activeModals.value, entry] : [entry]

    return id
  }

  const resolveModal = (id: string, callback: ModalCallback) => {
    const entry = activeModals.value.find((candidate) => candidate.id === id)

    if (entry) {
      const handler = entry[callback]
      activeModals.value = activeModals.value.filter((candidate) => candidate.id !== id)
      handler?.()
      return
    }

    const registration = registrations.value.find((candidate) => candidate.id === id)
    if (!registration) return

    if (eligibleProps(registration) === NOT_ELIGIBLE) return

    registration[callback]?.()
  }

  const cancelModal = (id: string) => resolveModal(id, 'onCancel')

  const closeModal = (id: string) => resolveModal(id, 'onClose')

  const confirmModal = (id: string) => resolveModal(id, 'onConfirm')

  const registerModal = <C extends Component>(registration: ModalRegistration<C>) => {
    const id = registration.id ?? uniqueId('modal-')

    const stored: StoredRegistration = {
      ...(registration as ModalRegistration),
      component: markRaw(registration.component as object) as Component,
      id,
    }

    // `registrations` is deep reactive, so the entry has to be unwrapped before
    // it can be looked up by identity.
    const replaced = registrations.value.find((entry) => entry.id === id)
    if (replaced) activationWatchers.get(toRaw(replaced))?.()

    const stopActivationWatch = watchActivation(stored)
    activationWatchers.set(stored, stopActivationWatch)

    registrations.value = [...registrations.value.filter((entry) => entry.id !== id), stored]

    return {
      id,
      unregister: () => {
        stopActivationWatch()
        activationWatchers.delete(stored)

        const { [id]: _dropped, ...rest } = activations.value
        activations.value = rest
        registrations.value = registrations.value.filter((entry) => entry.id !== id)
      },
    }
  }

  const imperativeCandidates = computed(() =>
    activeModals.value.map((entry) =>
      toCandidate(entry, entry.props as ModalProps, entry.activatedAt),
    ),
  )

  const declarativeCandidates = computed(() => {
    const eligible: ModalCandidate[] = []

    for (const registration of registrations.value) {
      const props = eligibleProps(registration)
      if (props === NOT_ELIGIBLE) continue

      eligible.push(toCandidate(registration, props, activations.value[registration.id] ?? 0))
    }

    return eligible
  })

  const candidates = computed(() =>
    [...imperativeCandidates.value, ...declarativeCandidates.value].sort(topmostFirst),
  )

  /**
   * Ordered bottom first, so the last entry is the topmost modal. The winning
   * candidate decides the mode: a modal that did not opt into stacking renders
   * on its own, which unmounts the stack it suppresses - those modals come back
   * as fresh mounts, without the state they had, once it resolves.
   */
  const resolvedModals = computed<ResolvedModal[]>(() => {
    const [winner] = candidates.value
    if (!winner) return []

    const rendered = winner.stack
      ? candidates.value.filter(({ stack }) => stack).slice(0, MODAL_STACK_MAX_SIZE)
      : [winner]

    return rendered.map(({ modal }, depth) => ({ ...modal, depth })).reverse()
  })

  return {
    activations,
    activeModals,
    cancelModal,
    closeModal,
    confirmModal,
    openModal,
    registerModal,
    registrations,
    resolvedModals,
  }
}
